import { formatValue, alignmentFor } from '@/lib/reportFormat';
import type {
  StandardReportPreview,
  StandardReportSection,
} from '@/types/standardReports.types';

/**
 * Renders a sectioned report result.
 *
 * This is the ONE component that knows what a section looks like. It is touched
 * when a section KIND is added — never when a report is. A new report arrives
 * with its own columns and sections and renders here without any change,
 * because nothing below is keyed on which report produced it.
 *
 * It deliberately does not use `DataTable`: that component owns sorting and
 * pagination over a flat row list, and a sectioned result is neither flat nor
 * sortable without breaking the subtotal each section prints. The sheet and
 * this view are meant to read the same way.
 */

interface Props {
  preview: StandardReportPreview;
  truncated?: boolean;
  totalRowCount?: number | null;
}

export function StandardReportSections({ preview, truncated, totalRowCount }: Props) {
  const { columns, sections, grandTotal, grandTotalLabel, headerNotes } = preview;

  return (
    <div className="space-y-6">
      {headerNotes?.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-4">
          {headerNotes.map((note) => (
            <div key={note.label} className="flex flex-col">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {note.label}
              </dt>
              <dd className="text-sm text-slate-900">{note.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {truncated && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Showing part of this report. The totals below cover{' '}
          <strong>all {totalRowCount?.toLocaleString() ?? 'matching'} rows</strong>, not just the
          rows on screen — download the workbook for the complete detail.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="sticky top-0 z-10 border-b border-slate-300 bg-slate-100 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                  style={{ textAlign: alignmentFor(column.type) }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sections.map((section) => (
              <SectionBlock key={section.key} section={section} columns={columns} />
            ))}
          </tbody>

          {grandTotal !== null && (
            <tfoot>
              <tr className="bg-slate-200 font-semibold">
                <td
                  className="border-t-2 border-slate-400 px-3 py-2 text-slate-900"
                  colSpan={Math.max(1, columns.length - 1)}
                >
                  {grandTotalLabel}
                </td>
                <td className="border-t-2 border-slate-400 px-3 py-2 text-right tabular-nums text-slate-900">
                  {formatValue(grandTotal, 'money')}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function SectionBlock({
  section,
  columns,
}: {
  section: StandardReportSection;
  columns: StandardReportPreview['columns'];
}) {
  const heldOut = !section.countsTowardTotal;
  const shownRows = section.rows.length;
  const partiallyShown = shownRows < section.totalRowCount;

  return (
    <>
      <tr>
        <td
          colSpan={columns.length}
          className={[
            'border-t border-slate-300 px-3 py-2 text-sm font-semibold',
            heldOut ? 'bg-slate-100 text-slate-600' : 'bg-slate-700 text-white',
          ].join(' ')}
        >
          <span>{section.label}</span>
          {/* Held-out sections are labelled as such so a reader is never left
              inferring why a block's money is missing from the closing total. */}
          {heldOut && (
            <span className="ml-2 text-xs font-normal">(not included in the total)</span>
          )}
          {partiallyShown && (
            <span className="ml-2 text-xs font-normal">
              showing {shownRows.toLocaleString()} of {section.totalRowCount.toLocaleString()}
            </span>
          )}
        </td>
      </tr>

      {/* What the gateway says this payout was, beside what our rows say it
          was. Rendered as a definition list rather than as cells: these
          describe the block, and putting them in the table's columns would
          invite a reader to sum them with the rows below. */}
      {section.notes && section.notes.length > 0 && (
        <tr>
          <td colSpan={columns.length} className="border-b border-slate-200 bg-slate-50 px-3 py-2">
            <dl className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
              {section.notes.map((note) => (
                <div key={note.label} className="flex flex-col">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {note.label}
                  </dt>
                  <dd className="text-sm tabular-nums text-slate-900">{note.value}</dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}

      {section.rows.map((row, index) => {
        // A voided row stays visible and is struck through: it is inside the
        // section and outside the subtotal, which the reader has to be able to
        // see rather than deduce from the arithmetic.
        const voided = row._isVoid === true;

        return (
          <tr
            key={`${section.key}-${index}`}
            className={voided ? 'text-slate-400 line-through' : 'odd:bg-white even:bg-slate-50'}
          >
            {columns.map((column) => (
              <td
                key={column.key}
                className="border-b border-slate-100 px-3 py-1.5 text-slate-700"
                style={{
                  textAlign: alignmentFor(column.type),
                  fontVariantNumeric: column.type === 'money' ? 'tabular-nums' : undefined,
                }}
              >
                {formatValue(row[column.key], column.type)}
              </td>
            ))}
          </tr>
        );
      })}

      {section.rows.length === 0 && (
        <tr>
          <td
            colSpan={columns.length}
            className="border-b border-slate-100 px-3 py-2 text-sm italic text-slate-400"
          >
            No rows
          </td>
        </tr>
      )}

      {section.subtotal !== null && (
        <tr className={heldOut ? 'bg-slate-50' : 'bg-slate-100'}>
          <td
            className="border-b border-slate-300 px-3 py-1.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-600"
            colSpan={Math.max(1, columns.length - 1)}
          >
            {heldOut ? 'Subtotal (excluded)' : 'Subtotal'}
          </td>
          <td className="border-b border-slate-300 px-3 py-1.5 text-right font-semibold tabular-nums text-slate-800">
            {formatValue(section.subtotal, 'money')}
          </td>
        </tr>
      )}
    </>
  );
}
